import {
  PRODUCT_DOWNLOADS, PRODUCT_PATHS, PRODUCT_SECTIONS, postHogConfig, productHost, productPayload,
  sendProductPayload, type ProductBrowserContext, type ProductEventName, type ProductEventProps, type PublicUser,
} from '@boardflight/shared';
import { api, heldSessionKnown, loadSession, onSessionChange, onSignIn, sessionHeldElsewhere, type Session } from './api';
import { ANALYTICS_PREFERENCE_EVENT, analyticsOptedOut } from './analytics-preferences';
import {
  analyticsSessionId, analyticsVisitorId, clearAnalyticsSession, forgetAnalyticsVisitor, knownAnalyticsVisitor,
} from './analytics-session';
import { findShell } from './storefront/link/bridge';
import { acrossFrom, surface } from './surface';

/**
 * The website's product analytics (`docs/product-analytics.md`): explicit events, sent only once
 * the page knows who it is speaking for.
 *
 * In a browser tab that is at once — the session is in this origin's storage, and whether its
 * account is on the exclusion list rides on the token pair. In the desktop app's window it is not:
 * the session is the native side's, the page learns who is signed in a moment after it loads, and
 * the holder's answer carries no exclusion. So there an event waits — until the holder has
 * answered, and for an account until `GET /v1/me` has said whether it is excluded — and goes when
 * both are known, or not at all. A page view that waited is sent once, as whoever turned out to be
 * here, rather than once as nobody and again as the account.
 */

let lastPage = '';
let lastUserId: string | null = null;

/** The desktop app's window, decided once: one load is one shell (as `storefront/welcome/reporting.ts` decides it). */
const inWindow = findShell() !== null;
/** How long the window waits for its holder before deciding nobody is coming — an app too old to hold a session. */
const WINDOW_WAIT_MS = 10_000;
let windowGiveUpAt = Number.POSITIVE_INFINITY;

/**
 * Events that arrived before the page knew who it was speaking for, oldest first. `userId` is who
 * each was for — an account, or null for nobody — and undefined for one made before anybody was
 * known, which is for whoever turns out to be here.
 */
type Held = { at: number; userId: string | null | undefined } & (
  | { kind: 'page'; path: string; hash: string }
  | { kind: 'event'; event: ProductEventName; props: unknown; distinctId?: string }
);
const HELD_MAX = 20;
let waiting: Held[] = [];

/** Whether the account in the window is on the exclusion list, once `GET /v1/me` has said. */
let exclusion: { userId: string; excluded: boolean } | null = null;
let asking: string | null = null;

/** Match complete known routes, replacing every parameter, query and fragment. */
export function productPage(path: string): (typeof PRODUCT_PATHS)[number] {
  const pathname = path.split(/[?#]/, 1)[0]!.replace(/\/apps(?=\/|$)/g, '/games');
  for (const template of PRODUCT_PATHS) {
    const parts = template.split('/');
    const actual = pathname.split('/');
    if (parts.length === actual.length && parts.every((part, i) => part.startsWith(':') ? Boolean(actual[i]) : part === actual[i])) return template;
  }
  // MCP callbacks can carry a provider-specific suffix. Never preserve that suffix.
  if (pathname.startsWith('/playground/oauth/callback/')) return '/playground/oauth/callback';
  return '/unknown';
}

export function productSection(hash: string): (typeof PRODUCT_SECTIONS)[number] | undefined {
  if (!hash) return undefined;
  const parts = hash.replace(/^#/, '').split('/');
  // Channel names are private, but the panel being used is a fixed vocabulary.
  const section = parts[0] === 'channels' && parts.length >= 3 ? parts[2]! : parts[0]!;
  return (PRODUCT_SECTIONS as readonly string[]).includes(section) ? section as (typeof PRODUCT_SECTIONS)[number] : 'unknown';
}

export function productReferrer(referrer: string): 'direct' | 'search' | 'social' | 'community' | 'internal' | 'other' {
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname;
    if (host === 'boardflight.app' || host.endsWith('.boardflight.app')) return 'internal';
    if (/(^|\.)(google\.[a-z.]+|bing.com|duckduckgo.com|search.yahoo.com)$/.test(host)) return 'search';
    if (/(^|\.)(discord.com|discord.gg|reddit.com|community.board.fun)$/.test(host)) return 'community';
    if (/(^|\.)(youtube.com|youtu.be|x.com|twitter.com|facebook.com|instagram.com|linkedin.com)$/.test(host)) return 'social';
  } catch { /* No raw referrer is sent. */ }
  return 'other';
}

function config() {
  return postHogConfig(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN, import.meta.env.VITE_POSTHOG_HOST);
}

/** Which host, and which shell, every browser event is from. */
function context(): ProductBrowserContext {
  let host = 'other';
  try { host = window.location.hostname; } catch { /* No location: neither host. */ }
  return { host: productHost(host), shell: inWindow ? 'desktop' : 'web' };
}

/**
 * Whether the signed-in account is on the exclusion list: true, false, or undefined while nobody
 * knows yet. A browser's session carries the answer; a held one is asked about, once per account.
 */
function excluded(session: Session): boolean | undefined {
  if (!sessionHeldElsewhere()) return session.analyticsExcluded === true;
  if (exclusion?.userId === session.userId) return exclusion.excluded;
  askExclusion(session.userId);
  return undefined;
}

function askExclusion(userId: string): void {
  if (asking === userId) return;
  asking = userId;
  void api<{ user: PublicUser }>('/v1/me').then(
    ({ user }) => {
      // Somebody else by the time it answered: what it said is not about them.
      if (user.id !== userId || loadSession()?.userId !== userId) return;
      exclusion = { userId, excluded: user.analyticsExcluded === true };
      flush();
    },
    // Unanswered is unknown, and unknown sends nothing identified. The next event asks again.
    () => undefined,
  ).finally(() => {
    if (asking === userId) asking = null;
  });
}

/** Whether `loadSession()` is the truth: always in a tab, and in the window once its holder has said. */
function identityKnown(): boolean {
  return !inWindow || heldSessionKnown() || Date.now() >= windowGiveUpAt;
}

/** Whether an event may go now: the page knows who it is for, and whether that account is counted. */
function ready(): boolean {
  if (!identityKnown()) return false;
  const session = loadSession();
  return session === null || excluded(session) !== undefined;
}

/** Who an event made now is about, or undefined while that is not known yet. */
function speakingFor(): string | null | undefined {
  return identityKnown() ? (loadSession()?.userId ?? null) : undefined;
}

function hold(item: Held): void {
  if (item.kind === 'page') {
    // The same page asked for twice while waiting — before the holder answered and after — is one view.
    waiting = waiting.filter((other) => other.kind !== 'page' || other.path !== item.path || other.hash !== item.hash);
  }
  waiting.push(item);
  if (waiting.length > HELD_MAX) waiting.shift();
}

/** Send what waited, now that the page knows who it is for. What was for somebody else is dropped. */
function flush(): void {
  if (waiting.length === 0 || !ready()) return;
  const now = loadSession()?.userId ?? null;
  const items = waiting;
  waiting = [];
  for (const item of items) {
    if (item.userId !== undefined && item.userId !== now) continue;
    if (item.kind === 'page') sendPage(item.path, item.hash, loadSession() !== null, item.at);
    else send(item.event, item.props as ProductEventProps<typeof item.event>, item.distinctId, item.at);
  }
}

function send<N extends ProductEventName>(event: N, props: ProductEventProps<N>, distinctId: string | undefined, at: number): void {
  const settings = config();
  if (!settings || analyticsOptedOut()) return;
  const session = loadSession();
  // An account on the analytics exclusion list sends nothing while it is signed in — not its pages,
  // not its sign-in, and no `$identify` tying this browser's earlier visits to it.
  if (session && excluded(session) !== false) return;
  const id = distinctId ?? (session ? `user:${session.userId}` : analyticsVisitorId());
  if (!id) return;
  const payload = productPayload(event, id, props, 'web', undefined, at, analyticsSessionId(), context());
  if (payload) void sendProductPayload(settings, payload, true).catch(() => undefined);
}

export function trackWebEvent<N extends ProductEventName>(
  event: N, props: ProductEventProps<N>, distinctId?: string,
): void {
  if (!config() || analyticsOptedOut()) return;
  if (!ready()) {
    hold({ kind: 'event', event, props, distinctId, at: Date.now(), userId: speakingFor() });
    return;
  }
  send(event, props, distinctId, Date.now());
}

function sendPage(path: string, hash: string, signedIn: boolean, at: number): void {
  const identity = loadSession()?.userId ?? 'anonymous';
  const key = `${path}:${hash}:${identity}`;
  if (key === lastPage) return; // React StrictMode must not double the bill or the page count.
  lastPage = key;
  send('$pageview', {
    path: productPage(path), section: productSection(hash), signed_in: signedIn, referrer: productReferrer(document.referrer),
  }, undefined, at);
}

/** Whether this page is about to be passed to the site's other host (`App.tsx`), which counts it there. */
function leaving(): boolean {
  try {
    return acrossFrom(surface(), window.location) !== null;
  } catch {
    return false;
  }
}

/**
 * Called from the rendered App, after its navigation guard accepts a move, and not while it is
 * resuming a session from the other host. A page on the wrong host is not counted: it is replaced
 * by the same page on the right one, and that one is the view: counting it here as well made one
 * visit to a link two.
 */
export function trackPageView(path: string, hash: string, signedIn: boolean): void {
  if (!config() || analyticsOptedOut() || leaving()) return;
  if (!ready()) {
    // Signed in or not is decided when it goes, by who turned out to be here.
    hold({ kind: 'page', path, hash, at: Date.now(), userId: speakingFor() });
    return;
  }
  sendPage(path, hash, signedIn, Date.now());
}

export function startProductAnalytics(): void {
  lastUserId = loadSession()?.userId ?? null;
  if (inWindow) {
    windowGiveUpAt = Date.now() + WINDOW_WAIT_MS;
    setTimeout(flush, WINDOW_WAIT_MS);
  }
  // Every way a session appears or goes. Nothing is *sent* from here: a session also appears when a
  // token is refreshed, another tab signs in or another host's session is resumed (`onSignIn`).
  onSessionChange((session) => {
    const next = session?.userId ?? null;
    if (next !== lastUserId) {
      // A shared browser must never connect two accounts through one anonymous visitor.
      if (lastUserId !== null) {
        forgetAnalyticsVisitor();
        clearAnalyticsSession();
      }
      lastUserId = next;
      lastPage = '';
    }
    flush();
  });
  // An actual sign-in, once, in the page it happened in: the anonymous visitor this browser was is
  // joined to the account, and the sign-in counted.
  onSignIn(() => {
    if (!config() || analyticsOptedOut()) return;
    const visitor = knownAnalyticsVisitor();
    if (visitor) trackWebEvent('$identify', { $anon_distinct_id: visitor });
    trackWebEvent('account_signed_in', {});
  });
  window.addEventListener(ANALYTICS_PREFERENCE_EVENT, () => {
    lastPage = '';
    if (analyticsOptedOut()) waiting = [];
  });
  document.addEventListener('click', (event) => {
    if (event.button !== 0 || !(event.target instanceof Element)) return;
    const link = event.target.closest('a[data-product-download]');
    const target = link?.getAttribute('data-product-download');
    if (target && (PRODUCT_DOWNLOADS as readonly string[]).includes(target)) {
      trackWebEvent('download_clicked', { target: target as (typeof PRODUCT_DOWNLOADS)[number] });
    }
  });
}
